import { useEffect, useMemo, useState } from 'react';
import { Settings } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import useJeMobil from '../hooks/useJeMobil';
import TabBar from '../components/layout/TabBar';
import { REZERVA_LISTY } from '../components/MobilniLista';
import SpravaSystem from '../components/sprava/SpravaSystem';
import SpravaFirma from '../components/sprava/SpravaFirma';
import SpravaIntegrace from '../components/sprava/SpravaIntegrace';
import SpravaProvozovna from '../components/sprava/SpravaProvozovna';
import SpravaUzivatele from '../components/sprava/SpravaUzivatele';
import { SECTION_COLOR, stylKarty, stylPole } from '../components/sprava/ui';

/**
 * Modul Správa (sekce Řízení) — revize Nastavení, 2026-10-12.
 *
 * Nastavení se rozpouští do modulů, které ho používají (⚙ v hlavičce modulu,
 * kategorie v hlavičce seznamu, vlastní záložky). Tady zůstává jen to, co
 * patří celé firmě. Záložky (klíče přístupů `sprava.*`):
 *  - uzivatele  → účty, šablony přístupů, PIN a pozvánky
 *  - provozovna → adresa a poloha, otevírací doba, svátky, účtenka, zařízení
 *  - firma      → identita firmy, podpis a razítko, e-mail účetní
 *  - integrace  → AI, SMS, Meta, Google — stav a tajné hodnoty jen pro zápis
 *  - system     → verze, zdravotní kontroly, příjemce reportu, DB sync
 *
 * Záložka jde z adresy (`#/sprava?tab=system`).
 */
// Záložky se seznamem a detailem (ModulLayout) — plná výška místo scrollu stránky
const SEZNAMOVE = ['uzivatele', 'integrace', 'provozovna'];

export default function Sprava({ user, initialTab = null, onNavigate = null }) {
    const { theme } = useTheme();
    const isDark = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
    const isMobile = useJeMobil();

    const [tab, setTab] = useState(initialTab);
    useEffect(() => { if (initialTab) setTab(initialTab); }, [initialTab]);

    const cardStyle = useMemo(() => stylKarty(isDark), [isDark]);
    const inputStyle = useMemo(() => stylPole(isDark), [isDark]);
    const spolecne = { user, isDark, isMobile, cardStyle, inputStyle, rezervaDole: isMobile ? REZERVA_LISTY : null, onPrepni: setTab, onNavigate };

    return (
        <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ flexShrink: 0 }}>
                {!isMobile && (
                    <div className="flex items-center gap-3 mb-4">
                        <div className="flex items-center justify-center shrink-0" style={{ width: 40, height: 40, borderRadius: 7, border: `1px solid ${SECTION_COLOR}40` }}>
                            <Settings size={20} style={{ color: SECTION_COLOR }} />
                        </div>
                        <div className="min-w-0">
                            <h1 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>Správa</h1>
                            <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>Uživatelé, provozovna, firma, integrace a systém</p>
                        </div>
                    </div>
                )}
                <TabBar modul="sprava" user={user} aktivni={tab} onZmena={setTab} barva={SECTION_COLOR} isDark={isDark} isMobile={isMobile} />
            </div>

            {/* Seznamové záložky (ModulLayout) mají plnou výšku a na mobilu se připnou k výřezu (§1b),
                kartové (Systém, Firma) scrollují jako stránka. */}
            {SEZNAMOVE.includes(tab) ? (
                <div style={{ flex: 1, minHeight: 0, paddingTop: isMobile ? 0 : 16, display: 'flex', flexDirection: 'column' }}>
                    <div className="flex flex-col" style={isMobile
                        ? { position: 'fixed', zIndex: 0, top: 'var(--hlavicka-mobil)', left: 0, right: 0, bottom: 0, overflow: 'hidden' }
                        : { height: '100%', minHeight: 0 }}>
                        {tab === 'uzivatele' && <SpravaUzivatele {...spolecne} />}
                        {tab === 'integrace' && <SpravaIntegrace {...spolecne} />}
                        {tab === 'provozovna' && <SpravaProvozovna {...spolecne} />}
                    </div>
                </div>
            ) : (
                <div style={{ flex: 1, minHeight: 0, paddingTop: isMobile ? 0 : 16, display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
                    {tab === 'system' && <SpravaSystem {...spolecne} />}
                    {tab === 'firma' && <SpravaFirma {...spolecne} />}
                </div>
            )}
        </div>
    );
}

